import styled from "@emotion/styled";
import Chart from "react-apexcharts";
import React, { useEffect, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import { ApexOptions } from "apexcharts";
import ko from "apexcharts/dist/locales/ko.json";
import { format } from "date-fns";
import { set } from "lodash";

type TDailyMeetingCount = {
  date: string;
  googleMeetingCount: number;
  mileMeetingCount: number;
};
type DailyReservationSourceChartProps = {
  data: TDailyMeetingCount[];
};
const DailyReservationSourceChart: React.FC<DailyReservationSourceChartProps> = ({ data = [] }) => {
  const globalStyle = getComputedStyle(document.documentElement);
  const [meetingCountChartOptions, setMeetingCountChartOptions] = useState<Object | null>(null);
  const [meetingCountSeries, setMeetingCountSeries] = useState<ApexOptions["series"]>([]);
  const [chartWidth, setChartWidth] = useState<number>(480);

  const setDataMeetingCountChart = (meetingCounts: TDailyMeetingCount[] | null) => {
    const monthMeetingCountDates: string[] = (meetingCounts ?? []).map((dailyMeetingCount) =>
      format(new Date(dailyMeetingCount.date), "M월 d일")
    );
    const monthGoogleMeetingCount: number[] = (meetingCounts ?? []).map(
      (dailyMeetingCount) => dailyMeetingCount.googleMeetingCount
    );
    const monthMileMeetingCount: number[] = (meetingCounts ?? []).map(
      (dailyMeetingCount) => dailyMeetingCount.mileMeetingCount
    );

    let chartSeries = [
      {
        name: "구글 예약 수",
        data: monthGoogleMeetingCount,
      },
      {
        name: "마일 예약 수",
        data: monthMileMeetingCount,
      },
    ];
    let chartOptions = {
      locales: [ko],
      defaultLocale: "ko",
      chart: {
        type: "bar",
        stacked: true,
        offsetY: 20,
        animations: {
          enabled: false, // 창 크기 변경에 의한 리렌더링 비활성화
        },
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 5,
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: [
        globalStyle.getPropertyValue("--global-color-lime-60"),
        globalStyle.getPropertyValue("--global-color-orange-70"),
      ],
      series: chartSeries,
      grid: {
        padding: {
          right: -5,
          bottom: 0,
          left: 0,
        },
      },
      xaxis: {
        categories: monthMeetingCountDates,
        labels: {
          show: true,
          style: {
            colors: "#A0A8B0",
            fontSize: "10px",
            fontWeight: 500,
            cssClass: "apexcharts-xaxis-label",
          },
        },
      },
      yaxis: {
        labels: {
          show: true,
          style: {
            colors: "#A0A8B0",
            fontSize: "10px",
            fontWeight: 500,
            cssClass: "apexcharts-yaxis-label",
          },
          rotate: 0,
        },
      },
      toolbar: {
        show: true,
      },
      legend: {
        position: "bottom",
        fontSize: "12px",
        fontWeight: 400,
        labels: {
          useSeriesColors: false,
          offsetX: -20,
          offsetY: 3,
        },
        itemMargin: {
          horizontal: 5,
        },
      },
    };

    setMeetingCountSeries(chartSeries);
    setMeetingCountChartOptions(chartOptions);
  };

  useEffect(() => {
    setDataMeetingCountChart(data ?? []);
    setChartWidth(Math.max(data.length * 20, 460)); // Adjust width based on data length
  }, [data]);

  return (
    <Container>
      <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-label-neutral)">
        일별 회의 현황
      </UI.Typography>
      <ContentContainer>
        {meetingCountChartOptions && meetingCountSeries && meetingCountSeries?.length > 0 && (
          <Chart
            options={meetingCountChartOptions ?? []}
            series={meetingCountSeries}
            type="bar"
            height={340}
            width={chartWidth}
          />
        )}
      </ContentContainer>
    </Container>
  );
};

export default React.memo(DailyReservationSourceChart);

const Container = styled.div`
  display: flex;
  width: 520px;
  height: 480px;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  overflow-x: auto;

  border-radius: 12px;
  border: 1px solid var(--alias-color-line-normal);
  background: var(--alias-color-background-normal-normal);
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow-x: auto;
`;
